import { useState, useEffect, useRef, useCallback } from 'react';

interface RealtimeDataOptions {
  endpoint: string;
  refreshInterval?: number;
  enabled?: boolean;
  onError?: (error: Error) => void;
}

interface RealtimeDataState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  lastUpdate: Date | null;
  isUpdating: boolean;
}

export function useRealtimeData<T>(
  options: RealtimeDataOptions
): RealtimeDataState<T> & { refresh: () => void } {
  const { endpoint, refreshInterval = 3000, enabled = true, onError } = options;
  
  const [state, setState] = useState<RealtimeDataState<T>>({
    data: null,
    isLoading: true,
    error: null,
    lastUpdate: null,
    isUpdating: false,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!mountedRef.current) return;

    setState(prev => ({ ...prev, isUpdating: true }));

    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      if (mountedRef.current) {
        setState({
          data,
          isLoading: false,
          error: null,
          lastUpdate: new Date(),
          isUpdating: false,
        });
      }
    } catch (error) {
      if (mountedRef.current) {
        const err = error instanceof Error ? error : new Error('Failed to fetch data');
        setState(prev => ({
          ...prev,
          error: err,
          isUpdating: false,
          isLoading: false,
        }));
        onError?.(err);
      }
    }
  }, [endpoint, onError]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!enabled) return;

    fetchData();

    if (refreshInterval > 0) {
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, refreshInterval, fetchData]);

  return { ...state, refresh };
}

export function useRealtimeMetrics() {
  const [metrics, setMetrics] = useState({
    totalSales: 0,
    totalOrders: 0,
    activeUsers: 0,
    conversionRate: 0,
    revenueGrowth: 0,
    pendingOrders: 0,
  });
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const updateMetrics = useCallback(() => {
    setIsUpdating(true);
    
    // Simulate real-time updates with random variations
    setTimeout(() => {
      setMetrics(prev => ({
        totalSales: prev.totalSales + Math.floor(Math.random() * 1000),
        totalOrders: prev.totalOrders + (Math.random() > 0.7 ? 1 : 0),
        activeUsers: Math.floor(100 + Math.random() * 50),
        conversionRate: +(2.5 + Math.random() * 2).toFixed(1),
        revenueGrowth: +(10 + Math.random() * 5).toFixed(1),
        pendingOrders: Math.floor(5 + Math.random() * 10),
      }));
      
      setLastUpdate(new Date());
      setIsUpdating(false);
    }, 300);
  }, []);

  useEffect(() => {
    // Initial fetch
    setMetrics({
      totalSales: 1234567,
      totalOrders: 1847,
      activeUsers: 127,
      conversionRate: 3.2,
      revenueGrowth: 15.3,
      pendingOrders: 12,
    });
    setLastUpdate(new Date());

    // Set up real-time updates every 3 seconds
    const interval = setInterval(updateMetrics, 3000);
    
    return () => clearInterval(interval);
  }, [updateMetrics]);

  return { metrics, isUpdating, lastUpdate, refresh: updateMetrics };
}
